import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import { ConfirmDialogComponent } from 'src/app/shared/modules/alerts/confirm-dialog/confirm-dialog.component';
import { SubscriptionPlanService } from 'src/app/shared/service/subscription-plan.service';
import { default as constants } from 'src/constant.json';
import { ProspektrApi } from '../api/prospektr.api';
import { CampaignCandidate } from '../interface/campaign.interface';
import { BackendResponse } from '../interface/common.interface';
import { CampaignState } from '../state/campaign.state';
import { SubscriptionState } from '../state/subscription.state';
import { UserState } from '../state/user.state';


@Injectable({
  providedIn: 'root',
})
export class CampaignService {
  constructor(
    private prospektrApi: ProspektrApi,
    private campaignState: CampaignState,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
    private subscriptionState: SubscriptionState,
    private appState: AppState,
    private userState: UserState,
    private subscriptionService: SubscriptionPlanService,
  ) { }

  fetchCampaigns(params?): void {
    this.campaignState.isLoadingCampaignListValue = true;
    this.prospektrApi
      .getCampaignList(params)
      .pipe(
        finalize(() => {
          this.campaignState.isLoadingCampaignListValue = false;
        })
      )
      .subscribe(
        (res) => {
          if (res && res.status == 'OK' && Array.isArray(res.data.data) && res.data.data.length) {
            this.campaignState.campaignListValue = res.data;
          } else {
            this.campaignState.campaignListValue = null;
          }
        },
        (err) => {
          this.openSnackBar(err.error.message, 'snackbar-error');
          this.campaignState.campaignListValue = null;
        }
      );
  }

  fetchCampaignsForAgentMarketing(params: any): void {
    this.campaignState.isLoadingAgentMarketingCampaignListValue = true;
    this.prospektrApi
      .getAgentMarketingCampaignList(params)
      .pipe(
        finalize(() => {
          this.campaignState.isLoadingAgentMarketingCampaignListValue = false;
        })
      )
      .subscribe(
        (res) => {
          if (res && res.status == 'OK' && res.data) {
            this.campaignState.agentMarketingCampaignListValue = res.data;
          } else {
            this.campaignState.agentMarketingCampaignListValue = null;
          }
        },
        (err) => {
          this.openSnackBar(err.error.message, 'snackbar-error');
          this.campaignState.agentMarketingCampaignListValue = null;
        }
      );
  }

  updateLeadCountForAutomatedCamp(params: any): void {
    this.prospektrApi
      .updateLeadForAutomatedCamp(params)
      .pipe(
        finalize(() => { })
      )
      .subscribe(
        () => {
          this.openSnackBar('Lead count updated successfully', 'snackbar-success');
        },
        (err) => {
          this.openSnackBar(err?.error?.message || 'An error occurred', 'snackbar-error');
        }
      );
  }



  getCampaignCandidates(campaignId: string): Observable<{ isLoading?: boolean; data?: Array<CampaignCandidate> }> {
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.prospektrApi
        .getCampaignCandidates(campaignId)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe((res: any) => {
          observer.next({ data: res.data });
        });
    });
  }

  createCampaign(params): Observable<any> {
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.prospektrApi
        .createCampaign(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: BackendResponse) => {
            observer.next({ data: res.data });
            this.fetchCampaigns();
            this.openSnackBar(res.message, 'snackbar-success');
          },
          (err) => {
            this.openSnackBar(err?.error?.message || 'Failed to create campaign', 'snackbar-error');
          }
        );
    });
  }

  updateCampaign(campaignId, campaign): Observable<any> {
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.prospektrApi
        .updateCampaign(campaignId, campaign)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: BackendResponse) => {
            observer.next({ data: res.data ? res.data : campaignId });
            this.fetchCampaigns();
            this.openSnackBar(res.message, 'snackbar-success');
          },
          (err) => {
            this.openSnackBar('Failed to update campaign', 'snackbar-error');
          }
        );
    });
  }

  createCampaignUsers(params, notify = true): Observable<any> {
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.prospektrApi
        .createCampaignUsers(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res) => {
            if (notify) {
              this.openSnackBar(res.message, 'snackbar-success');
            }
            this.campaignState.isLoadingAddCampaignUserValue = false;
          },
          (err) => {
            let message = 'Failed to create campaign';
            if (err && err.error && err.error.message) message = err.error.message;
            this.openSnackBar(message, 'snackbar-error');
            this.campaignState.isLoadingAddCampaignUserValue = false;
          }
        );
    });
  }

  launchCampaign(campaignId: string, frequency: string = null, sendImmediately) {
    return new Observable((observer) => {

      if (!this.subscriptionService.checkFeatureValidity(constants.PLAN_BASED_FEATURES.campaignAccess.key)) return;

      // if (this.appState.appCompanyAliasValue == 'valuetest') {
      //   if (
      //     this.userState?.getUserPlanStatusValue?.planType != 'subscription' &&
      //     !this.userState.userDetails?.roles.includes('companyManager')
      //   ) {
      //     this.openSnackBar(
      //       'The option to create/launch campaign is only available with our monthly subscription plan. Please upgrade your plan to access this feature.',
      //       'snackbar-warning'
      //     );
      //   }
      // } else if (
      //   this.subscriptionState.planUsageValue.campaignUsage >= this.subscriptionState.planUsageValue.campaignLimit
      // ) {
      //   this.openSnackBar('Please upgrade your plan', 'snackbar-warning');
      //   return observer.complete();
      // } else {
      const planUsage = this.subscriptionState.planUsageValue;
      (planUsage.campaignUsage as number)++;
      this.subscriptionState.planUsageValue = planUsage;
      // }

      this.prospektrApi
        .launchCampaign(campaignId, frequency, sendImmediately)
        .pipe(
          finalize(() => {
            observer.next();
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            this.fetchCampaigns();
            if (sendImmediately == true) this.openSnackBar(res.message, 'snackbar-success');
          },
          (err) => {
            this.openSnackBar(err.error.message || 'Failed to launch campaign', 'snackbar-error');
            this.fetchCampaigns();
          }
        );
    });
  }

  launchAutomatedCampaign(params, notify = true): Observable<any> {
    return new Observable((observer) => {

      if (!this.subscriptionService.checkFeatureValidity(constants.PLAN_BASED_FEATURES.campaignAccess.key)) return;

      // if (this.appState.appCompanyAliasValue == 'valuetest') {
      //   if (
      //     this.userState?.getUserPlanStatusValue?.planType != 'subscription' &&
      //     !this.userState.userDetails?.roles.includes('companyManager')
      //   ) {
      //     this.openSnackBar(
      //       'The option to create/launch campaign is only available with our monthly subscription plan. Please upgrade your plan to access this feature.',
      //       'snackbar-warning'
      //     );
      //   }
      // } else if (
      //   this.subscriptionState.planUsageValue.campaignUsage >= this.subscriptionState.planUsageValue.campaignLimit
      // ) {
      //   this.openSnackBar('Please upgrade your plan', 'snackbar-warning');
      //   return observer.complete();
      // } else {
      const planUsage = this.subscriptionState.planUsageValue;
      (planUsage.campaignUsage as number)++;
      this.subscriptionState.planUsageValue = planUsage;
      // }

      this.prospektrApi
        .launchAutomatedCampaign(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res) => {
            if (notify) {
              this.openSnackBar(res.message, 'snackbar-success');
            }
            this.campaignState.isLoadingAddCampaignUserValue = false;
          },
          (err) => {
            let message = 'Failed to create campaign';
            if (err && err.error && err.error.message) message = err.error.message;
            this.openSnackBar(message, 'snackbar-error');
            this.campaignState.isLoadingAddCampaignUserValue = false;
          }
        );
    });
  }

  deleteCampaign(campaignId: string) {
    this.prospektrApi.deleteCampaign(campaignId).subscribe(
      (res: BackendResponse) => {
        this.openSnackBar(res.message, 'snackbar-success');
        this.fetchCampaigns();
      },
      (err) => {
        this.openSnackBar('Failed to delete campaign', 'snackbar-error');
      }
    );
  }

  openDeleteCampaignDialog(id) {
    let message: string;
    const dialogRef = this.matDialog.open(ConfirmDialogComponent, {
      width: '385px',
      disableClose: true,
      hasBackdrop: true,
      data: {
        type: 'deleteCampaign',
        message: 'The selected campaign will be deleted',
        title: 'Do you want to delete?',
        proceedAction: (status, callBack) => {
          if (status) {
            this.deleteCampaign(id);
            callBack({ status: true });
            dialogRef.close();
          } else {
            return false;
          }
        },
        proceedCancelAction: () => {
          dialogRef.close();
        },
      },
    });
  }

  updateCampaignUsers(params, notify = true) {
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.prospektrApi
        .updateCampaignUsers(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res) => {
            if (notify) {
              this.openSnackBar(res.message, 'snackbar-success');
            }
          },
          (err) => {
            this.openSnackBar(err.error.message || 'Failed to update campaign', 'snackbar-error');
          }
        );
    });
  }

  getCampaignInfo(params): Observable<any> {
    return new Observable((observer) => {
      let result = [];
      observer.next({ isLoading: true });
      this.prospektrApi
        .campaignInfo(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false, response: result });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            result = res.data;
            if (params.campaignId) {
              if (res && res.data) {
                this.campaignState.campaignInfoValue = res;
              } else {
                this.campaignState.campaignInfoValue = null;
              }
            }
          },
          (err) => {
            this.campaignState.campaignInfoValue = null;
          }
        );
    });
  }

  getAgentMarketingCampaignInfo(params): Observable<any> {
    return new Observable((observer) => {
      let result = [];
      observer.next({ isLoading: true });
      this.prospektrApi
        .agentMarketingCampaignInfo(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false, response: result });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            result = res.data;
            if (params.campaignId) {
              if (res && res.data) {
                this.campaignState.agentMarketingCampaignInfoValue = res;
              } else {
                this.campaignState.agentMarketingCampaignInfoValue = null;
              }
            }
          },
          (err) => {
            this.campaignState.agentMarketingCampaignInfoValue = null;
          }
        );
    });
  }

  getCampaignReadInfo(params): Observable<any> {
    return new Observable((observer) => {
      let result = [];
      observer.next({ isLoading: true });
      this.prospektrApi
        .campaignReadInfo(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false, response: result });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            result = res.data;
          },
          (err) => {
            console.log('getReportInfo : ', err);
          }
        );
    });
  }

  getReportInfo(params): Observable<any> {
    return new Observable((observer) => {
      let result = [];
      observer.next({ isLoading: true });
      this.prospektrApi
        .reportInfo(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false, response: result });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            result = res.data;
          },
          (err) => {
            console.log('getReportInfo : ', err);
          }
        );
    });
  }

  getAgentMarketingCampaignStatisticsExport(params): Observable<any> {
    return new Observable((observer) => {
      observer.next({ isLoading: true });

      this.prospektrApi
        .agentMarketingCampaignStatisticsExport(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (csvString: string) => {
            try {
              const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8;' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              const name = params?.campaignId?.date
                ? 'campaign_export_' + params.campaignId.date + '.csv'
                : 'campaign_export.csv';
              link.setAttribute('download', name);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
              observer.next({ response: 'downloaded' });
            } catch (err) {
              observer.next({ isLoading: false, error: err });
              observer.complete();
            }
          },
          (err) => {
            observer.next({ isLoading: false, error: err });
            observer.complete();
          }
        );
    });
  }

  private openSnackBar(message, style) {
    if (message) {
      this.snackBar.open(message, '', {
        duration: 3000,
        panelClass: [style ? style : ''],
        verticalPosition: 'top',
      });
    }
  }
}
